/**
 * Reviews — public "leave us a review" + aggregate display page.
 *
 * Route: /reviews
 *
 * Landlords (and any signed-in user) can submit a 1-5 star review of
 * Central Rentals. Submissions persist via POST /api/reviews/submit and
 * are held for owner moderation. Approved reviews stream back through
 * GET /api/reviews/public + /api/reviews/aggregate which feed the page
 * + the AggregateRating schema.org JSON-LD payload below, so Google can
 * pick up the rich-result stars on the SERP.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, ExternalLink, ShieldCheck, Quote } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SITE = "https://centralrentals.ca";

const GOOGLE_REVIEW_URL =
  "https://search.google.com/local/writereview?placeid=ChIJ-Central-Rentals-Canada";

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(sel.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

// Reusable star rating control. `value` 0-5, integer. `onChange` is only
// invoked when interactive=true. Renders fractional half-stars when the
// `value` is a non-integer (e.g. an average like 4.7).
export function StarRating({ value = 0, onChange, interactive = false, size = 20, testid }) {
  const [hover, setHover] = useState(0);
  const displayed = hover || value;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid} role="radiogroup" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = displayed >= n;
        const half = !filled && displayed >= n - 0.5;
        const ariaChecked = Math.round(value) === n;
        const StarIcon = (
          <Star
            className={`transition-colors ${
              filled
                ? "fill-amber-400 text-amber-400"
                : half
                ? "fill-amber-200 text-amber-400"
                : "fill-transparent text-slate-300"
            }`}
            style={{ width: size, height: size }}
          />
        );
        if (!interactive) return <span key={n}>{StarIcon}</span>;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={ariaChecked}
            aria-label={`${n} ${n === 1 ? "star" : "stars"}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange?.(n)}
            data-testid={`${testid || "star"}-${n}`}
            className="p-0.5 hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
          >
            {StarIcon}
          </button>
        );
      })}
    </div>
  );
}

function AggregateBanner({ aggregate }) {
  // When there are 0 reviews, skip the aggregate banner — the list below
  // already shows a single, clearer "No published reviews yet · Be the
  // first →" empty state. Rendering both was visually redundant.
  if (!aggregate || !aggregate.count) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-5" data-testid="aggregate-banner">
      <div>
        <div className="font-display text-5xl font-extrabold text-slate-900 tabular-nums" data-testid="aggregate-average">
          {Number(aggregate.average || 0).toFixed(1)}
        </div>
        <div className="mt-1"><StarRating value={Number(aggregate.average || 0)} size={18}/></div>
        <div className="text-xs text-slate-500 mt-1" data-testid="aggregate-count">
          based on {aggregate.count} landlord {aggregate.count === 1 ? "review" : "reviews"}
        </div>
      </div>
      <div className="flex-1 hidden md:block border-l border-slate-200 pl-5 text-sm text-slate-600">
        Verified reviews from Canadian landlords using Central Rentals across all 13 provinces and territories.
      </div>
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`review-card-${r.id || r.name}`}>
      <div className="flex items-start gap-3">
        {r.avatar_url ? (
          <img src={r.avatar_url} alt={r.name} className="w-10 h-10 rounded-full object-cover flex-none" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold flex-none">
            {(r.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-slate-900 truncate" data-testid="review-name">{r.name}</div>
            {r.location && (
              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{r.location}</span>
            )}
            {r.source === "google" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                <ExternalLink className="w-2.5 h-2.5"/> Google
              </span>
            )}
          </div>
          <StarRating value={r.rating || 0} size={14} testid="review-stars"/>
          <Quote className="w-4 h-4 text-emerald-600 mt-3 mb-1"/>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="review-text">{r.text}</p>
        </div>
      </div>
    </article>
  );
}

function LeaveReviewForm({ onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [form, setForm] = useState({ name: "", email: "", location: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.text.trim() || rating < 1) {
      setError("Please give a star rating and fill in name, email, and your review.");
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/reviews/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          location: form.location.trim(),
          rating,
          text: form.text.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setDone(true);
      toast.success("Thanks — we'll review and publish shortly.");
      onSubmitted?.();
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="review-submitted">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2"/>
        <div className="font-display text-lg font-bold text-slate-900">Thanks for the kind words!</div>
        <p className="text-sm text-slate-600 mt-1">We review every submission before publishing — yours will show up here within 24 hours.</p>
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-on-google"
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">
          <ExternalLink className="w-3.5 h-3.5"/> Also leave it on Google
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="leave-review-form">
      <div>
        <div className="font-display text-lg font-extrabold text-slate-900">Leave a review</div>
        <p className="text-xs text-slate-500 mt-0.5">Built for Canadian landlords by a Canadian landlord. Tell other landlords what's working — or what we should fix.</p>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Your rating</label>
        <StarRating value={rating} onChange={setRating} interactive size={32} testid="leave-review-rating"/>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your name *</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="leave-review-name"
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email *</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="leave-review-email"
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">City, Province (optional)</label>
        <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder="e.g. Edmonton, AB"
          data-testid="leave-review-location"
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your review *</label>
        <textarea required rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
          maxLength={1200}
          placeholder="What do you like about Central Rentals? What would you tell another Canadian landlord considering us?"
          data-testid="leave-review-text"
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        <div className="text-[10px] text-slate-400 text-right">{form.text.length}/1200</div>
      </div>

      {error && <div className="text-xs text-rose-600" data-testid="leave-review-error">{error}</div>}

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
        <button type="submit" disabled={busy} data-testid="leave-review-submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Submitting…</> : <>Submit review</>}
        </button>
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-review-google"
          className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold text-sm">
          <ExternalLink className="w-3.5 h-3.5"/> Also post to Google
        </a>
      </div>

      <p className="text-[10px] text-slate-400">By submitting you agree we may quote your review on the site. We never share your email.</p>
    </form>
  );
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pubR, aggR] = await Promise.all([
        fetch(`${API}/api/reviews/public?limit=50`).then((r) => r.json()).catch(() => ({ items: [] })),
        fetch(`${API}/api/reviews/aggregate`).then((r) => r.json()).catch(() => ({ count: 0, average: null })),
      ]);
      setReviews(pubR.items || []);
      setAggregate(aggR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    document.title = "Reviews — Central Rentals Canada";
    const tags = [
      setMeta('meta[name="description"]', {
        name: "description",
        content: "Read what Canadian landlords say about Central Rentals — and leave your own review of the property OS for the Canadian landlord.",
      }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: `${SITE}/reviews` }),
    ];
    return () => tags.forEach((t) => t?.remove?.());
  }, []);

  // schema.org AggregateRating JSON-LD — only emit when there are reviews
  // so Google never sees a 0-rating SoftwareApplication entity.
  const jsonLd = aggregate?.count
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Central Rentals Canada",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: aggregate.average,
          reviewCount: aggregate.count,
          bestRating: 5,
          worstRating: 1,
        },
        review: reviews.slice(0, 5).map((r) => ({
          "@type": "Review",
          reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
          author: { "@type": "Person", name: r.name },
          reviewBody: r.text,
        })),
      }
    : null;

  return (
    <div className="bg-white min-h-screen" data-testid="reviews-page">
      <PublicHeader light={true} />
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-10 pb-6">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2">
          <ShieldCheck className="w-3 h-3"/> Verified Canadian landlord reviews
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tighter">
          Reviews
        </h1>
        <p className="mt-3 text-base text-slate-600 max-w-2xl">
          What Canadian landlords say about Central Rentals. Leave your own below — we read every one and reply within 24 hours.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-6">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto"/></div>
        ) : (
          <AggregateBanner aggregate={aggregate}/>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-5 gap-8 pb-16">
        <div className="lg:col-span-3 space-y-4" data-testid="reviews-list">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 inline"/></div>
          ) : reviews.length === 0 ? null : (
            reviews.map((r, i) => <ReviewCard key={r.id || `${r.name}-${i}`} r={r}/>)
          )}
        </div>

        <aside className={`${reviews.length === 0 ? "lg:col-span-5" : "lg:col-span-2"}`} id="leave">
          <LeaveReviewForm onSubmitted={load}/>
        </aside>
      </section>
    </div>
  );
}
